// @ts-nocheck
/**
 * Country Code Direction form — redesigned to obd3-gui pattern (ScrollPage
 * + PageHeader + Card + shadcn Switch). Dual-mode via `isEdit` prop;
 * excludeId query param on edit; current country injected into options if
 * the available-countries API filters it out.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { ArrowLeft, Flag, Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import {
  API_COUNTRY_CODE_DIRECTIONS_MASTER_AVAILABLE_COUNTRIES,
  API_COUNTRY_CODE_DIRECTIONS_MASTER_CREATE,
  API_COUNTRY_CODE_DIRECTIONS_MASTER_UPDATE,
  API_COUNTRY_CODE_DIRECTIONS_MASTER_SINGLE,
} from '@/service/url';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { ROUTES } from '@/utilities/constant';

const CountryCodeDirectionForm = ({ isEdit = false }: any) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [countryCodes, setCountryCodes] = useState<any[]>([]);
  const [countryCodeId, setCountryCodeId] = useState<any>(null);
  const [isEnabled, setIsEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [currentCountry, setCurrentCountry] = useState<any>(null);

  useEffect(() => {
    const url =
      isEdit && id
        ? `${API_COUNTRY_CODE_DIRECTIONS_MASTER_AVAILABLE_COUNTRIES}?excludeId=${id}`
        : API_COUNTRY_CODE_DIRECTIONS_MASTER_AVAILABLE_COUNTRIES;

    getApiCall(url, {})
      .then((data: any) => setCountryCodes(Array.isArray(data) ? data : []))
      .catch(() => setCountryCodes([]));

    if (isEdit && id) {
      getApiCall(API_COUNTRY_CODE_DIRECTIONS_MASTER_SINGLE(id), {})
        .then((resp: any) => {
          setCountryCodeId(resp?.country_code_id);
          setIsEnabled(resp?.is_enabled !== false);
          if (resp?.country_code) setCurrentCountry(resp.country_code);
        })
        .catch((err: any) =>
          toast.error(friendlyApiError(err) || 'Failed to load direction'),
        );
    }
  }, [id, isEdit]);

  const countryOptions = countryCodes.map((c: any) => ({
    value: c.id,
    label: `${c.name} ${c.calling_code ? `+${c.calling_code}` : ''}`,
  }));

  if (
    isEdit &&
    currentCountry &&
    !countryOptions.some((o: any) => o.value === currentCountry.id)
  ) {
    countryOptions.unshift({
      value: currentCountry.id,
      label: `${currentCountry.name} ${
        currentCountry.calling_code ? `+${currentCountry.calling_code}` : ''
      }`,
    });
  }

  const submit = (e?: any) => {
    e?.preventDefault();
    if (!countryCodeId) {
      toast.error('Select country');
      return;
    }
    setBusy(true);
    const payload = { country_code_id: Number(countryCodeId), is_enabled: !!isEnabled };
    const url = isEdit
      ? API_COUNTRY_CODE_DIRECTIONS_MASTER_UPDATE(id)
      : API_COUNTRY_CODE_DIRECTIONS_MASTER_CREATE;
    postApiCall(url, payload)
      .then(() => {
        toast.success(isEdit ? 'Updated' : 'Created');
        navigate(`/${ROUTES.COUNTRY_CODE_DIRECTIONS.LIST}`);
      })
      .catch((err: any) => {
        toast.error(friendlyApiError(err) || 'Failed to save direction');
        setBusy(false);
      });
  };

  return (
    <ScrollPage>
      <PageHeader
        title={isEdit ? 'Edit Country Code' : 'Add Country Code'}
        description={
          isEdit
            ? 'Update whether this country code is enabled for outbound.'
            : 'Enable an additional country calling code for outbound direction.'
        }
        actions={
          <Button asChild variant="outline">
            <Link to={`/${ROUTES.COUNTRY_CODE_DIRECTIONS.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />

      <form className="space-y-4" onSubmit={submit}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Flag className="h-4 w-4 text-primary" />
              Country
            </CardTitle>
            <CardDescription>Select the country and whether outbound is enabled.</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-end">
            <div className="space-y-2">
              <Label>
                Country <span className="text-destructive">*</span>
              </Label>
              <Select
                className="theme-react-select"
                classNamePrefix="theme-react-select"
                options={countryOptions}
                value={countryOptions.find((o: any) => o.value === countryCodeId) || null}
                onChange={(opt: any) => setCountryCodeId(opt?.value || null)}
                placeholder="Select country"
              />
            </div>
            <div className="flex items-center gap-3 pb-1.5">
              <Switch
                id="cc-enabled"
                checked={isEnabled}
                onCheckedChange={(v: any) => setIsEnabled(!!v)}
              />
              <Label htmlFor="cc-enabled" className="cursor-pointer">
                {isEnabled ? 'Enabled' : 'Disabled'}
              </Label>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="ghost">
            <Link to={`/${ROUTES.COUNTRY_CODE_DIRECTIONS.LIST}`}>Cancel</Link>
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {busy ? (isEdit ? 'Updating…' : 'Creating…') : isEdit ? 'Save changes' : 'Create'}
          </Button>
        </div>
      </form>
    </ScrollPage>
  );
};

export default CountryCodeDirectionForm;
